import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Row,
  Select,
  Typography,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import modalStyles from '@/modules/zy-crm/components/business-modal/style.module.less';
import { FormSectionDivider } from '@/modules/zy-crm/components/form-section-divider';
import { MemberPicker } from '@/modules/zy-crm/components/member-picker';
import {
  SharedProductFormSection,
  type SharedProductRow,
} from '@/modules/zy-crm/components/shared-product-form-section';
import { resolveCrmScopedViewModeFromPathname } from '@/modules/zy-crm/utils/crm-view';
import { resolveRelationText } from '@/modules/zy-crm/utils/display';
import { getResponsiveModalWidth } from '@/modules/zy-crm/utils/modal-width';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';
import { fetchContactOptions } from '@/modules/zy-crm/utils/relation-options';
import { queryContractTypes } from '@/services/bff/contract.api';
import { queryOpportunityDrawerProfile } from '@/services/bff/opportunity.api';
import type { ContractTypeItem } from '@/services/bff/types/contract';
import { useOpenPlatformConfig } from '@/services/openPlatformToken';
import { createAppRequest } from '@/services/request';
import {
  type DetailResponse,
  extractDetail,
  extractList,
  type ListResponse,
} from '@/utils/apiResponse';

const request = createAppRequest();
const { Text } = Typography;
const MemberPickerField = MemberPicker as unknown as React.FC<any>;
const NEW_PRODUCT_ROW_KEY = '__new_product__';

function getCurrentPathname() {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.pathname || '';
}

type RowData = Record<string, any>;
type OptionItem = { label: string; value: string; raw?: any };
type ProductRow = SharedProductRow;

type EditableProductRow = ProductRow & {
  isNew?: boolean;
};

type OpportunityProductLike = {
  uuid?: string;
  productUuid?: string;
  productName?: string;
  productUnit?: string | number | null;
  productPrice?: string | number | null;
  opportunityPrice?: string | number | null;
  quantity?: string | number | null;
  productType?: string | number | null;
  listPrice?: string | number | null;
  salePrice?: string | number | null;
  costPrice?: string | number | null;
  offeringUuid?: string;
  offeringVersionUuid?: string;
  productCode?: string;
  versionNo?: string;
  standardPrice?: string | number | null;
  selectionSnapshot?: Record<string, unknown>;
};

export type ContractEditModalMode = 'create' | 'edit';

export type ContractEditInitialValues = {
  ownerUserUuid?: string;
  customerUuid?: string;
  customerName?: string;
  opportunityUuid?: string;
  opportunityName?: string;
  name?: string;
  sourceQuoteUuid?: string;
  sourceQuoteName?: string;
  products?: ProductRow[];
};

export type ContractEditContext = {
  ownerUserUuid?: string;
  customerUuid?: string;
  customerName?: string;
  opportunityUuid?: string;
  opportunityName?: string;
  contactUuid?: string;
  contactName?: string;
  signContactUuid?: string;
  contractUuid?: string;
  contractName?: string;
  sourceObjectType?: 'CUSTOMER' | 'OPPORTUNITY' | 'QUOTATION';
};

type ContractEditModalProps = {
  open: boolean;
  mode: ContractEditModalMode;
  recordUuid?: string;
  initialValues?: ContractEditInitialValues;
  context?: ContractEditContext;
  zIndex?: number;
  onCancel: () => void;
  onSuccess?: () => void | Promise<void>;
};

const i18nKeys = {
  modalCreateTitle: 'pages.zy-crm.contract.modal.createTitle',
  modalEditTitle: 'pages.zy-crm.contract.modal.editTitle',
  formRequired: 'pages.zy-crm.contract.form.required',
  formPlaceholderInput: 'pages.zy-crm.contract.form.placeholder.input',
  formPlaceholderSelect: 'pages.zy-crm.contract.form.placeholder.select',
  formGroupBasic: 'pages.zy-crm.contract.form.group.basic',
  formGroupSign: 'pages.zy-crm.contract.form.group.sign',
  formGroupProduct: 'pages.zy-crm.contract.form.group.product',
  formAutoCodeHint: 'pages.zy-crm.contract.form.autoCodeHint',
  messageCreateSuccess: 'pages.zy-crm.contract.message.createSuccess',
  messageUpdateSuccess: 'pages.zy-crm.contract.message.updateSuccess',
  messageCustomerLoadFailed: 'pages.zy-crm.contract.message.customerLoadFailed',
  messageOpportunityLoadFailed:
    'pages.zy-crm.contract.message.opportunityLoadFailed',
  messageLoadDetailFailed: 'pages.zy-crm.contract.message.loadDetailFailed',
  messageProductEmpty: 'pages.zy-crm.contract.message.productEmpty',
  actionDelete: 'pages.zy-crm.contract.action.delete',
  columnOperation: 'pages.zy-crm.contract.column.operation',
};

const fieldKeys = {
  code: 'pages.zy-crm.contract.field.code',
  name: 'pages.zy-crm.contract.field.name',
  customerUuid: 'pages.zy-crm.contract.field.customerUuid',
  opportunityUuid: 'pages.zy-crm.contract.field.opportunityUuid',
  ownerUserUuid: 'pages.zy-crm.contract.field.ownerUserUuid',
  orderDate: 'pages.zy-crm.contract.field.orderDate',
  startTime: 'pages.zy-crm.contract.field.startTime',
  endTime: 'pages.zy-crm.contract.field.endTime',
  contractType: 'pages.zy-crm.contract.field.contractType',
  companySignerUserUuid: 'pages.zy-crm.contract.field.companySignerUserUuid',
  signContactUuid: 'pages.zy-crm.contract.field.signContactUuid',
  remark: 'pages.zy-crm.contract.field.remark',
  totalProductPrice: 'pages.zy-crm.contract.field.totalProductPrice',
  discountPercent: 'pages.zy-crm.contract.field.discountPercent',
  totalPrice: 'pages.zy-crm.contract.field.totalPrice',
  profitAmount: 'pages.zy-crm.contract.field.profitAmount',
  productName: 'pages.zy-crm.contract.field.productName',
  productType: 'pages.zy-crm.contract.field.productType',
  productUnit: 'pages.zy-crm.contract.field.productUnit',
  listPrice: 'pages.zy-crm.contract.field.listPrice',
  costPrice: 'pages.zy-crm.contract.field.costPrice',
  salePrice: 'pages.zy-crm.contract.field.salePrice',
  quantity: 'pages.zy-crm.contract.field.quantity',
  lineTotal: 'pages.zy-crm.contract.field.lineTotal',
};

const CONTRACT_TYPE_OPTIONS = [
  {
    value: 1,
    key: 'pages.zy-crm.contract.enum.type.standard',
    fallback: '标准合同',
  },
  {
    value: 2,
    key: 'pages.zy-crm.contract.enum.type.framework',
    fallback: '框架合同',
  },
  {
    value: 3,
    key: 'pages.zy-crm.contract.enum.type.supplement',
    fallback: '补充协议',
  },
];

function formatFallbackMessage(
  defaultMessage: string,
  values?: Record<string, any>,
) {
  if (!values) {
    return defaultMessage;
  }
  return defaultMessage.replace(/\{(\w+)\}/g, (_match, key: string) => {
    const value = values[key];
    return value === undefined || value === null ? '' : String(value);
  });
}

const toNumber = (value: unknown) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  return n;
};

const toAmount = (value: number) => value.toFixed(2);

const toDateValue = (value: unknown): Dayjs | undefined => {
  if (!value) return undefined;
  const d = dayjs(String(value));
  return d.isValid() ? d : undefined;
};

const mapToDatePayload = (value: Dayjs | string | undefined) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  return value.format('YYYY-MM-DD');
};

const normalizeProductRows = (rows: ProductRow[]) => {
  return rows.map((row) => {
    const quantity = toNumber(row.quantity);
    const salePrice = toNumber(row.salePrice);
    const lineTotal = quantity * salePrice;
    return {
      ...row,
      productName: row.productName || '',
      productType: row.productType || undefined,
      productUnit: row.productUnit || undefined,
      quantity: row.quantity ?? '1',
      listPrice: row.listPrice ?? row.salePrice ?? '0',
      costPrice: row.costPrice ?? row.salePrice ?? '0',
      salePrice: row.salePrice ?? '0',
      lineTotal: toAmount(lineTotal),
    };
  });
};

export const mapOpportunityProductsToContractRows = (
  opportunityUuid: string,
  products: OpportunityProductLike[],
) =>
  normalizeProductRows(
    products.map((item, index) => ({
      key: String(
        item?.uuid || item?.productUuid || `${opportunityUuid}-${index}`,
      ),
      productUuid: item?.productUuid,
      offeringUuid: item?.offeringUuid,
      offeringVersionUuid: item?.offeringVersionUuid,
      productCode: item?.productCode,
      versionNo: item?.versionNo,
      standardPrice:
        item?.standardPrice == null ? undefined : String(item.standardPrice),
      selectionSnapshot: item?.selectionSnapshot,
      productName: item?.productName || '',
      productType:
        item?.productType === null || item?.productType === undefined
          ? undefined
          : String(item.productType),
      productUnit:
        item?.productUnit === undefined || item?.productUnit === null
          ? undefined
          : String(item.productUnit),
      listPrice: String(
        item?.listPrice ??
          item?.productPrice ??
          item?.salePrice ??
          item?.opportunityPrice ??
          '0',
      ),
      costPrice: String(item?.costPrice ?? item?.productPrice ?? '0'),
      salePrice: String(
        item?.salePrice ??
          item?.opportunityPrice ??
          item?.listPrice ??
          item?.productPrice ??
          '0',
      ),
      quantity: String(item?.quantity ?? '1'),
    })),
  );

export const normalizeContractProductRows = (rows: ProductRow[]) =>
  normalizeProductRows(rows);

const normalizeEditableProductRow = (
  row?: Partial<EditableProductRow>,
): EditableProductRow => {
  const normalized = normalizeProductRows([
    {
      key: String(row?.key || '').trim() || NEW_PRODUCT_ROW_KEY,
      productUuid: row?.productUuid,
      offeringUuid: row?.offeringUuid,
      offeringVersionUuid: row?.offeringVersionUuid,
      productCode: row?.productCode,
      versionNo: row?.versionNo,
      standardPrice: row?.standardPrice,
      selectionSnapshot: row?.selectionSnapshot,
      productName: row?.productName || '',
      productType: row?.productType,
      productUnit: row?.productUnit,
      listPrice: row?.listPrice ?? '0.00',
      costPrice: row?.costPrice ?? '0.00',
      salePrice: row?.salePrice ?? '0.00',
      quantity: row?.quantity ?? '1.00',
      lineTotal: row?.lineTotal ?? '0.00',
    },
  ])[0];

  return {
    ...normalized,
    isNew: Boolean(row?.isNew),
  };
};

const toEditableProductRow = (
  row?: Partial<SharedProductRow>,
): EditableProductRow => {
  const next = row || {};
  return normalizeEditableProductRow({
    key: next.key,
    productUuid: next.productUuid,
    offeringUuid: next.offeringUuid,
    offeringVersionUuid: next.offeringVersionUuid,
    productCode: next.productCode,
    versionNo: next.versionNo,
    standardPrice: next.standardPrice,
    selectionSnapshot: next.selectionSnapshot,
    productName: next.productName || '',
    productType: next.productType,
    productUnit: next.productUnit,
    listPrice: next.listPrice ?? '0.00',
    costPrice: next.costPrice ?? '0.00',
    salePrice: next.salePrice ?? '0.00',
    quantity: next.quantity ?? '1.00',
    lineTotal: next.lineTotal ?? '0.00',
  });
};

const mapQuoteProductToContractRow = (
  item: any,
  fallbackKey: string,
): ProductRow => ({
  key: String(item?.uuid || item?.productUuid || fallbackKey),
  productUuid: item?.productUuid,
  offeringUuid: item?.offeringUuid,
  offeringVersionUuid: item?.offeringVersionUuid,
  productCode: item?.productCode,
  versionNo: item?.versionNo,
  standardPrice:
    item?.standardPrice == null ? undefined : String(item.standardPrice),
  selectionSnapshot: item?.selectionSnapshot,
  productName: item?.productName || '',
  productType: item?.productType,
  productUnit: item?.productUnit,
  listPrice: String(item?.listPrice ?? item?.salePrice ?? '0'),
  costPrice: String(item?.costPrice ?? item?.productPrice ?? '0'),
  salePrice: String(item?.salePrice ?? item?.quotePrice ?? '0'),
  quantity: String(item?.quantity ?? '1'),
});

const mapContractProductToRow = (
  item: any,
  fallbackKey: string,
): ProductRow => ({
  key: String(item?.uuid || item?.productUuid || fallbackKey),
  productUuid: item?.productUuid,
  offeringUuid: item?.offeringUuid,
  offeringVersionUuid: item?.offeringVersionUuid,
  productCode: item?.productCode,
  versionNo: item?.versionNo,
  standardPrice:
    item?.standardPrice == null ? undefined : String(item.standardPrice),
  selectionSnapshot: item?.selectionSnapshot,
  productName: item?.productName || '',
  productType: item?.productType,
  productUnit: item?.productUnit,
  listPrice: String(
    item?.listPrice ?? item?.salePrice ?? item?.contractPrice ?? '0',
  ),
  costPrice: String(item?.costPrice ?? item?.productPrice ?? '0'),
  salePrice: String(item?.salePrice ?? item?.contractPrice ?? '0'),
  quantity: String(item?.quantity ?? '1'),
});

const toContractApiProductRow = (row: ProductRow) => ({
  uuid: row.key && row.key !== NEW_PRODUCT_ROW_KEY ? row.key : undefined,
  productUuid: row.productUuid || row.offeringUuid,
  offeringUuid: row.offeringUuid || row.productUuid,
  offeringVersionUuid: row.offeringVersionUuid,
  productCode: row.productCode,
  versionNo: row.versionNo,
  standardPrice: row.standardPrice,
  selectionSnapshot: row.selectionSnapshot,
  productName: row.productName,
  productType: row.productType,
  productUnit: row.productUnit,
  listPrice: toAmount(toNumber(row.listPrice)),
  costPrice: toAmount(toNumber(row.costPrice)),
  salePrice: toAmount(toNumber(row.salePrice)),
  quantity: toAmount(toNumber(row.quantity)),
});

const hasMeaningfulProductRows = (rows: ProductRow[]) =>
  rows.some(
    (item) =>
      (item.productUuid || item.productName) && toNumber(item.quantity) > 0,
  );

export const shouldLoadContractCustomers = (keyword?: string) =>
  !!String(keyword || '').trim();

export const shouldLoadContractOpportunities = (customerUuid?: string) =>
  !!String(customerUuid || '').trim();

export const shouldLoadContractQuotes = (customerUuid?: string) =>
  !!String(customerUuid || '').trim();

export const mergeOptionItems = (
  options: OptionItem[],
  extra?: OptionItem | null,
) => {
  if (!extra?.value) {
    return options;
  }
  return options.some((item) => item.value === extra.value)
    ? options
    : [extra, ...options];
};

export const buildOptionItem = (
  value: unknown,
  label?: unknown,
  raw?: any,
): OptionItem | null => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) {
    return null;
  }
  return {
    value: normalizedValue,
    label: String(label || normalizedValue).trim() || normalizedValue,
    raw,
  };
};

export function ContractEditModal({
  open,
  mode,
  recordUuid,
  initialValues,
  context,
  zIndex,
  onCancel,
  onSuccess,
}: ContractEditModalProps) {
  const openPlatformConfig = useOpenPlatformConfig();
  const pageViewMode = resolveCrmScopedViewModeFromPathname(
    getCurrentPathname(),
    'contracts',
  );
  const [form] = Form.useForm<RowData>();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState<RowData | null>(null);
  const [customerOptions, setCustomerOptions] = useState<OptionItem[]>([]);
  const [contactOptions, setContactOptions] = useState<OptionItem[]>([]);
  const [opportunityOptions, setOpportunityOptions] = useState<OptionItem[]>(
    [],
  );
  const [quoteOptions, setQuoteOptions] = useState<OptionItem[]>([]);
  const [contractTypeOptions, setContractTypeOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [customerNameMap, setCustomerNameMap] = useState<
    Record<string, string>
  >({});
  const [productRows, setProductRows] = useState<SharedProductRow[]>([]);
  const [editingProductKey, setEditingProductKey] = useState<string>();
  const [productDraftRow, setProductDraftRow] = useState<
    EditableProductRow | undefined
  >(undefined);
  const sourceQuoteSyncRef = useRef(false);
  const watchedSourceQuoteUuid = Form.useWatch('sourceQuoteUuid', form);
  const watchedDiscountPercent = Form.useWatch('discountPercent', form);
  const initialSourceQuoteLocked =
    mode === 'create' && !!String(initialValues?.sourceQuoteUuid || '').trim();
  const initialOpportunityLocked =
    mode === 'create' &&
    !!String(initialValues?.opportunityUuid || '').trim() &&
    !!String(initialValues?.opportunityName || '').trim();

  const t = (
    _id: string,
    defaultMessage: string,
    values?: Record<string, any>,
  ) => formatFallbackMessage(defaultMessage, values);

  const normalizedContext = useMemo(
    () => ({
      ownerUserUuid: String(context?.ownerUserUuid || '').trim() || undefined,
      customerUuid: String(context?.customerUuid || '').trim() || undefined,
      opportunityUuid:
        String(context?.opportunityUuid || '').trim() || undefined,
      signContactUuid:
        String(context?.signContactUuid || context?.contactUuid || '').trim() ||
        undefined,
      name:
        String(initialValues?.name || '').trim() ||
        (String(context?.opportunityName || '').trim()
          ? `${String(context?.opportunityName).trim()}合同`
          : String(context?.customerName || '').trim()
            ? `${String(context?.customerName).trim()}合同`
            : undefined),
    }),
    [
      context?.contactUuid,
      context?.customerName,
      context?.customerUuid,
      context?.opportunityName,
      context?.opportunityUuid,
      context?.ownerUserUuid,
      context?.signContactUuid,
      initialValues?.name,
    ],
  );

  const fieldText = {
    code: t(fieldKeys.code, '合同编号'),
    name: t(fieldKeys.name, '合同名称'),
    customerUuid: t(fieldKeys.customerUuid, '客户名称'),
    opportunityUuid: t(fieldKeys.opportunityUuid, '商机名称'),
    ownerUserUuid: t(fieldKeys.ownerUserUuid, '负责人'),
    orderDate: t(fieldKeys.orderDate, '下单时间'),
    startTime: t(fieldKeys.startTime, '合同开始日期'),
    endTime: t(fieldKeys.endTime, '合同结束日期'),
    contractType: t(fieldKeys.contractType, '合同类型'),
    companySignerUserUuid: t(fieldKeys.companySignerUserUuid, '公司签约人'),
    signContactUuid: t(fieldKeys.signContactUuid, '客户签约人'),
    remark: t(fieldKeys.remark, '备注'),
    totalProductPrice: t(fieldKeys.totalProductPrice, '产品成本总金额'),
    discountPercent: t(fieldKeys.discountPercent, '整单折扣(%)'),
    totalPrice: t(fieldKeys.totalPrice, '合同总额'),
    profitAmount: t(fieldKeys.profitAmount, '利润'),
    productName: t(fieldKeys.productName, '产品名称'),
    productType: t(fieldKeys.productType, '产品类型'),
    productUnit: t(fieldKeys.productUnit, '产品单位'),
    listPrice: t(fieldKeys.listPrice, '价格'),
    costPrice: t(fieldKeys.costPrice, '成本价'),
    salePrice: t(fieldKeys.salePrice, '售价'),
    quantity: t(fieldKeys.quantity, '数量'),
    lineTotal: t(fieldKeys.lineTotal, '小计'),
  };

  const modalWidth = getResponsiveModalWidth('wideForm');

  const totalProductPrice = useMemo(() => {
    return productRows.reduce((sum, row) => sum + toNumber(row.lineTotal), 0);
  }, [productRows]);
  const discountPercent = toNumber(watchedDiscountPercent);
  const totalPrice = totalProductPrice * Math.max(0, 1 - discountPercent / 100);
  const totalCost = productRows.reduce(
    (sum, row) => sum + toNumber(row.costPrice) * toNumber(row.quantity),
    0,
  );
  const profitAmount = totalPrice - totalCost;
  const normalizedProductRows = useMemo(
    () => normalizeProductRows(productRows),
    [productRows],
  );
  const editableProductRows = useMemo<EditableProductRow[]>(() => {
    const rows = normalizedProductRows.map((item) =>
      normalizeEditableProductRow({
        ...item,
        isNew: false,
      }),
    );
    if (editingProductKey === NEW_PRODUCT_ROW_KEY && productDraftRow) {
      return [...rows, productDraftRow];
    }
    return rows.map((item) =>
      productDraftRow && item.key === editingProductKey
        ? productDraftRow
        : item,
    );
  }, [editingProductKey, normalizedProductRows, productDraftRow]);
  const selectedSourceQuote = useMemo(
    () =>
      quoteOptions.find(
        (item) => item.value === String(watchedSourceQuoteUuid || ''),
      )?.raw,
    [quoteOptions, watchedSourceQuoteUuid],
  );
  const sourceQuoteCustomerName = resolveRelationText({
    value: selectedSourceQuote?.customerUuid,
    directName:
      selectedSourceQuote?.customerName ||
      selectedSourceQuote?.customerDisplayName,
    nameMap: customerNameMap,
  });
  const sourceQuoteOpportunityName =
    opportunityOptions.find(
      (item) =>
        item.value === String(selectedSourceQuote?.opportunityUuid || ''),
    )?.label ||
    selectedSourceQuote?.opportunityName ||
    selectedSourceQuote?.opportunityDisplayName ||
    selectedSourceQuote?.opportunityUuid ||
    '-';
  const currentCustomerUuid = String(
    form.getFieldValue('customerUuid') || '',
  ).trim();
  const opportunityPlaceholder = currentCustomerUuid
    ? t(i18nKeys.formPlaceholderSelect, '请选择{label}', {
        label: fieldText.opportunityUuid,
      })
    : '请先选择客户名称';
  const quotePlaceholder = currentCustomerUuid
    ? '请选择来源报价单'
    : '请先选择客户名称';

  const loadCustomers = async (keyword = '') => {
    if (!shouldLoadContractCustomers(keyword)) {
      setCustomerOptions([]);
      return [];
    }
    try {
      const resp = await request<ListResponse<RowData>>(
        '/bff/zy-crm/customers',
        {
          method: 'GET',
          params: { page: 1, pageSize: 30, keyword: keyword || undefined },
        },
      );
      const list = extractList(resp);
      const options = list
        .map((item: any) => ({
          value: String(item?.uuid || ''),
          label: String(item?.name || item?.uuid || ''),
          raw: item,
        }))
        .filter((item: OptionItem) => !!item.value);
      setCustomerOptions(options);
      setCustomerNameMap((prev) => {
        const next = { ...prev };
        list.forEach((item: any) => {
          const id = String(item?.uuid || '');
          const name = String(item?.name || '');
          if (id && name) next[id] = name;
        });
        return next;
      });
      return options;
    } catch (_error) {
      message.error(
        t(i18nKeys.messageCustomerLoadFailed, '客户列表加载失败，请稍后重试'),
      );
      return [];
    }
  };

  const loadOpportunities = async (keyword = '', customerUuid?: string) => {
    const normalizedCustomerUuid = String(customerUuid || '').trim();
    if (!shouldLoadContractOpportunities(normalizedCustomerUuid)) {
      setOpportunityOptions([]);
      return [];
    }
    try {
      const resp = await request<ListResponse<RowData>>(
        '/bff/zy-crm/opportunities',
        {
          method: 'GET',
          params: {
            page: 1,
            pageSize: 30,
            keyword: keyword || undefined,
            customerUuid: normalizedCustomerUuid,
          },
        },
      );
      const list = extractList(resp);
      const options = list
        .map((item: any) => ({
          value: String(item?.uuid || ''),
          label: String(item?.name || item?.uuid || ''),
          raw: item,
        }))
        .filter((item: OptionItem) => !!item.value);
      setOpportunityOptions(options);
      return options;
    } catch (_error) {
      message.error(
        t(
          i18nKeys.messageOpportunityLoadFailed,
          '商机列表加载失败，请稍后重试',
        ),
      );
      return [];
    }
  };

  const loadContacts = async (
    keyword = '',
    customerUuid?: string,
  ): Promise<OptionItem[]> => {
    try {
      const options = await fetchContactOptions(
        keyword || undefined,
        customerUuid || undefined,
      );
      setContactOptions(
        options.map((item) => ({
          value: String(item.value || ''),
          label: String(item.label || item.value || ''),
          raw: item.raw,
        })),
      );
      return options.map((item) => ({
        value: String(item.value || ''),
        label: String(item.label || item.value || ''),
        raw: item.raw,
      }));
    } catch (_error) {
      message.error('联系人列表加载失败，请稍后重试');
      return [];
    }
  };

  const loadQuotes = async (
    keyword = '',
    customerUuid?: string,
    opportunityUuid?: string,
  ) => {
    const normalizedCustomerUuid = String(customerUuid || '').trim();
    if (!shouldLoadContractQuotes(normalizedCustomerUuid)) {
      setQuoteOptions([]);
      return [];
    }
    try {
      const resp = await request<ListResponse<RowData>>('/bff/zy-crm/quotes', {
        method: 'GET',
        params: {
          page: 1,
          pageSize: 30,
          keyword: keyword || undefined,
          customerUuid: normalizedCustomerUuid,
          opportunityUuid: opportunityUuid || undefined,
        },
      });
      const list = extractList(resp);
      const options = list
        .map((item: any) => ({
          value: String(item?.uuid || ''),
          label: String(item?.name || item?.code || item?.uuid || ''),
          raw: item,
        }))
        .filter((item: OptionItem) => !!item.value);
      setQuoteOptions(options);
      return options;
    } catch (_error) {
      message.error('报价列表加载失败，请稍后重试');
      return [];
    }
  };

  const applyOpportunityProducts = async (opportunityUuid?: string) => {
    const normalizedUuid = String(opportunityUuid || '').trim();
    if (!normalizedUuid) {
      setProductRows([]);
      return;
    }
    try {
      const profile = await queryOpportunityDrawerProfile(normalizedUuid, {
        viewMode: pageViewMode,
      });
      const rows = mapOpportunityProductsToContractRows(
        normalizedUuid,
        profile?.related?.products?.list || [],
      );
      if (!rows.length) {
        message.warning('当前商机下没有可带入的产品明细');
        return;
      }
      setProductRows(rows);
      message.success('已带入商机产品明细');
    } catch (_error) {
      message.error('商机产品明细加载失败，请稍后重试');
    }
  };

  const handleOpportunityChange = async (value?: string) => {
    const nextUuid = String(value || '').trim();
    if (!nextUuid) {
      if (!hasMeaningfulProductRows(productRows)) {
        setProductRows([]);
      }
      return;
    }
    if (!hasMeaningfulProductRows(productRows)) {
      await applyOpportunityProducts(nextUuid);
      return;
    }
    const confirmed = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '替换产品明细确认',
        content: '切换商机后，将用商机产品覆盖当前合同明细，是否继续？',
        okText: '继续替换',
        cancelText: '保留当前明细',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (confirmed) {
      await applyOpportunityProducts(nextUuid);
    }
  };

  const applyQuoteProducts = async (quoteUuid?: string) => {
    const normalizedUuid = String(quoteUuid || '').trim();
    if (!normalizedUuid) return;
    try {
      const detail = await request<DetailResponse<RowData> | RowData>(
        `/bff/zy-crm/quotes/${normalizedUuid}`,
        { method: 'GET' },
      );
      const payload = extractDetail(detail);
      if (!payload) {
        throw new Error('empty quote detail');
      }
      const rows = normalizeProductRows(
        (payload?.products || []).map((item: any, idx: number) =>
          mapQuoteProductToContractRow(item, `${normalizedUuid}-${idx}`),
        ),
      );
      if (!rows.length) {
        message.warning('当前报价单下没有可带入的产品明细');
        return;
      }
      setProductRows(rows);
      message.success('已带入报价产品明细');
    } catch (_error) {
      message.error('报价产品明细加载失败，请稍后重试');
    }
  };

  const handleQuoteChange = async (value?: string) => {
    const nextUuid = String(value || '').trim();
    if (!nextUuid) return;
    const selectedQuote = quoteOptions.find(
      (item) => item.value === nextUuid,
    )?.raw;
    const nextCustomerUuid = String(selectedQuote?.customerUuid || '').trim();
    const nextOpportunityUuid = String(
      selectedQuote?.opportunityUuid || '',
    ).trim();
    const currentCustomerUuid = String(
      form.getFieldValue('customerUuid') || '',
    ).trim();
    const currentOpportunityUuid = String(
      form.getFieldValue('opportunityUuid') || '',
    ).trim();

    const shouldOverwriteRelation =
      (nextCustomerUuid &&
        currentCustomerUuid &&
        nextCustomerUuid !== currentCustomerUuid) ||
      (nextOpportunityUuid &&
        currentOpportunityUuid &&
        nextOpportunityUuid !== currentOpportunityUuid);

    if (shouldOverwriteRelation) {
      const confirmedRelation = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          closable: { 'aria-label': '关闭' },
          keyboard: true,
          title: '替换关联信息确认',
          content:
            '选择报价单后，将同步覆盖当前合同的客户和商机关联，是否继续？',
          okText: '继续覆盖',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmedRelation) {
        form.setFieldValue('sourceQuoteUuid', undefined);
        return;
      }
    }

    if (nextCustomerUuid || nextOpportunityUuid) {
      sourceQuoteSyncRef.current = true;
      form.setFieldsValue({
        customerUuid: nextCustomerUuid || undefined,
        opportunityUuid: nextOpportunityUuid || undefined,
        sourceQuoteUuid: nextUuid,
      });
      void Promise.all([
        loadOpportunities('', nextCustomerUuid || undefined),
        loadQuotes(
          '',
          nextCustomerUuid || undefined,
          nextOpportunityUuid || undefined,
        ),
      ]).finally(() => {
        sourceQuoteSyncRef.current = false;
      });
    }
    if (!hasMeaningfulProductRows(productRows)) {
      await applyQuoteProducts(nextUuid);
      return;
    }
    const confirmed = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '替换产品明细确认',
        content: '选择报价单后，将用报价产品覆盖当前合同明细，是否继续？',
        okText: '继续替换',
        cancelText: '保留当前明细',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (confirmed) {
      await applyQuoteProducts(nextUuid);
    }
  };

  const addProductRow = () => {
    if (editingProductKey) {
      message.warning('请先保存或取消当前编辑的产品');
      return;
    }
    setEditingProductKey(NEW_PRODUCT_ROW_KEY);
    setProductDraftRow(
      normalizeEditableProductRow({
        key: NEW_PRODUCT_ROW_KEY,
        productName: '',
        quantity: '1.00',
        listPrice: '0.00',
        costPrice: '0.00',
        salePrice: '0.00',
        lineTotal: '0.00',
        isNew: true,
      }),
    );
  };

  const removeProductRow = (key: string) => {
    setProductRows((prev) => prev.filter((item) => item.key !== key));
  };

  const stopProductEditing = () => {
    setEditingProductKey(undefined);
    setProductDraftRow(undefined);
  };

  const handleProductDraftChange = (
    patch:
      | Partial<EditableProductRow>
      | ((current: EditableProductRow) => Partial<EditableProductRow>),
  ) => {
    setProductDraftRow((current) => {
      const base = normalizeEditableProductRow(current);
      const nextPatch = typeof patch === 'function' ? patch(base) : patch;
      return normalizeEditableProductRow({
        ...base,
        ...nextPatch,
        key: base.key,
        isNew: base.isNew,
      });
    });
  };

  const beginEditProductRow = (record: SharedProductRow) => {
    if (editingProductKey && editingProductKey !== record.key) {
      message.warning('请先保存或取消当前编辑的产品');
      return;
    }
    setEditingProductKey(record.key);
    setProductDraftRow(
      normalizeEditableProductRow({
        ...record,
        isNew: false,
      }),
    );
  };

  const saveProductRow = () => {
    if (!productDraftRow || !editingProductKey) {
      return;
    }
    if (!String(productDraftRow.productUuid || '').trim()) {
      message.error('请选择产品名称');
      return;
    }
    const nextRow = normalizeProductRows([
      {
        ...productDraftRow,
        key:
          editingProductKey === NEW_PRODUCT_ROW_KEY
            ? `${Date.now()}-${Math.random()}`
            : editingProductKey,
      },
    ])[0];

    if (editingProductKey === NEW_PRODUCT_ROW_KEY) {
      setProductRows((prev) => [...prev, nextRow]);
    } else {
      setProductRows((prev) =>
        normalizeProductRows(
          prev.map((item) => {
            if (item.key !== editingProductKey) return item;
            return nextRow;
          }),
        ),
      );
    }
    stopProductEditing();
  };

  const deleteProductRow = (record: SharedProductRow) => {
    if (record.key === NEW_PRODUCT_ROW_KEY) {
      stopProductEditing();
      return;
    }
    removeProductRow(record.key);
    if (editingProductKey === record.key) {
      stopProductEditing();
    }
  };

  const resetModalState = () => {
    setEditingRecord(null);
    setProductRows([]);
    setEditingProductKey(undefined);
    setProductDraftRow(undefined);
    setCustomerOptions([]);
    setContactOptions([]);
    setOpportunityOptions([]);
    setQuoteOptions([]);
    form.resetFields();
  };

  useEffect(() => {
    if (!open) {
      resetModalState();
      return;
    }

    const initialize = async () => {
      setLoading(true);
      try {
        const types = await queryContractTypes({ pageNo: 1, pageSize: 100 });
        setContractTypeOptions(
          (types || []).map((item: ContractTypeItem) => ({
            value: item.uuid,
            label: item.name,
          })),
        );
        if (mode === 'edit' && recordUuid) {
          const detail = await request<DetailResponse<RowData> | RowData>(
            `/bff/zy-crm/contracts/${recordUuid}`,
            {
              method: 'GET',
            },
          );
          const payload = extractDetail(detail);
          if (!payload) {
            throw new Error('empty contract detail');
          }
          const rawOwnerValue = String(payload?.ownerUserUuid || '').trim();
          const customerOption = buildOptionItem(
            payload?.customerUuid,
            payload?.customerName || payload?.customerDisplayName,
            {
              uuid: payload?.customerUuid,
              name: payload?.customerName || payload?.customerDisplayName,
            },
          );
          const opportunityOption = buildOptionItem(
            payload?.opportunityUuid,
            payload?.opportunityName || payload?.opportunityDisplayName,
            {
              uuid: payload?.opportunityUuid,
              name: payload?.opportunityName || payload?.opportunityDisplayName,
            },
          );
          const quoteOption = buildOptionItem(
            payload?.sourceQuoteUuid,
            payload?.sourceQuoteName ||
              payload?.sourceQuoteDisplayName ||
              payload?.sourceQuoteCode,
            {
              uuid: payload?.sourceQuoteUuid,
              name:
                payload?.sourceQuoteName ||
                payload?.sourceQuoteDisplayName ||
                payload?.sourceQuoteCode,
              customerUuid: payload?.customerUuid,
              opportunityUuid: payload?.opportunityUuid,
              customerName:
                payload?.customerName || payload?.customerDisplayName,
              opportunityName:
                payload?.opportunityName || payload?.opportunityDisplayName,
            },
          );
          setEditingRecord(payload);
          setCustomerOptions(customerOption ? [customerOption] : []);
          setOpportunityOptions(opportunityOption ? [opportunityOption] : []);
          setQuoteOptions(quoteOption ? [quoteOption] : []);
          form.setFieldsValue({
            ...payload,
            ownerUserUuid: rawOwnerValue || undefined,
            orderDate: toDateValue(payload?.orderDate),
            startTime: toDateValue(payload?.startTime),
            endTime: toDateValue(payload?.endTime),
          });
          setProductRows(
            normalizeProductRows(
              (payload?.products || []).map((item: any, idx: number) =>
                mapContractProductToRow(item, String(idx + 1)),
              ),
            ),
          );
          await Promise.all([
            loadContacts('', payload?.customerUuid),
            payload?.customerUuid
              ? loadOpportunities('', payload?.customerUuid).then((options) =>
                  setOpportunityOptions(
                    mergeOptionItems(options, opportunityOption),
                  ),
                )
              : Promise.resolve(),
            payload?.customerUuid
              ? loadQuotes(
                  '',
                  payload?.customerUuid,
                  payload?.opportunityUuid,
                ).then((options) =>
                  setQuoteOptions(mergeOptionItems(options, quoteOption)),
                )
              : Promise.resolve(),
          ]);
          return;
        }

        const createValues = {
          discountPercent: 0,
          ...initialValues,
          ownerUserUuid:
            initialValues?.ownerUserUuid || normalizedContext.ownerUserUuid,
          customerUuid:
            initialValues?.customerUuid || normalizedContext.customerUuid,
          opportunityUuid:
            initialValues?.opportunityUuid || normalizedContext.opportunityUuid,
          signContactUuid: normalizedContext.signContactUuid,
          name: initialValues?.name || normalizedContext.name,
          sourceQuoteUuid: initialValues?.sourceQuoteUuid,
        };
        const customerOption = buildOptionItem(
          createValues.customerUuid,
          initialValues?.customerName || context?.customerName,
          {
            uuid: createValues.customerUuid,
            name: initialValues?.customerName || context?.customerName,
          },
        );
        const opportunityOption = buildOptionItem(
          createValues.opportunityUuid,
          initialValues?.opportunityName || context?.opportunityName,
          {
            uuid: createValues.opportunityUuid,
            name: initialValues?.opportunityName || context?.opportunityName,
          },
        );
        const quoteOption = buildOptionItem(
          createValues.sourceQuoteUuid,
          initialValues?.sourceQuoteName,
          {
            uuid: createValues.sourceQuoteUuid,
            name: initialValues?.sourceQuoteName,
            customerUuid: createValues.customerUuid,
            opportunityUuid: createValues.opportunityUuid,
            customerName: initialValues?.customerName || context?.customerName,
            opportunityName:
              initialValues?.opportunityName || context?.opportunityName,
          },
        );
        setCustomerOptions(customerOption ? [customerOption] : []);
        setOpportunityOptions(opportunityOption ? [opportunityOption] : []);
        setQuoteOptions(quoteOption ? [quoteOption] : []);
        form.setFieldsValue(createValues);
        setProductRows(normalizeProductRows(initialValues?.products || []));
        await Promise.all([
          loadContacts('', createValues.customerUuid),
          createValues.customerUuid
            ? loadOpportunities('', createValues.customerUuid).then((options) =>
                setOpportunityOptions(
                  mergeOptionItems(options, opportunityOption),
                ),
              )
            : Promise.resolve(),
          createValues.customerUuid
            ? loadQuotes(
                '',
                createValues.customerUuid,
                createValues.opportunityUuid,
              ).then((options) =>
                setQuoteOptions(mergeOptionItems(options, quoteOption)),
              )
            : Promise.resolve(),
        ]);
        if (
          !initialValues?.products?.length &&
          createValues.opportunityUuid &&
          !createValues.sourceQuoteUuid
        ) {
          await applyOpportunityProducts(createValues.opportunityUuid);
        }
      } catch (_error) {
        message.error(t(i18nKeys.messageLoadDetailFailed, '加载合同详情失败'));
      } finally {
        setLoading(false);
      }
    };

    void initialize();
  }, [form, initialValues, mode, normalizedContext, open, recordUuid]);

  const submitModal = async () => {
    if (editingProductKey) {
      message.error('请先保存当前编辑中的产品');
      return;
    }
    const values = await form.validateFields();
    const rows = normalizeProductRows(productRows).filter(
      (item) =>
        item.productUuid && item.productName && toNumber(item.quantity) > 0,
    );
    if (!rows.length) {
      message.error(t(i18nKeys.messageProductEmpty, '请至少添加一个产品明细'));
      return;
    }

    const selectedType = contractTypeOptions.find(
      (item) => item.value === String(values.contractTypeUuid || ''),
    );
    const payload: Record<string, unknown> = {
      ...values,
      orderDate: mapToDatePayload(values.orderDate),
      startTime: mapToDatePayload(values.startTime),
      endTime: mapToDatePayload(values.endTime),
      discountPercent: toAmount(discountPercent),
      products: rows.map(toContractApiProductRow),
      contractTypeName: selectedType?.label,
    };
    if (mode === 'create') {
      delete payload.contractType;
      if (
        context?.sourceObjectType === 'CUSTOMER' &&
        !values.sourceQuoteUuid &&
        !values.opportunityUuid &&
        values.customerUuid
      ) {
        payload.sourceObjectType = 'CUSTOMER';
        payload.sourceObjectUuid = values.customerUuid;
      }
    }

    if (values?.sourceQuoteUuid) {
      const selectedQuote = quoteOptions.find(
        (item) => item.value === String(values.sourceQuoteUuid),
      )?.raw;
      const quoteCustomerUuid = String(
        selectedQuote?.customerUuid || '',
      ).trim();
      const quoteOpportunityUuid = String(
        selectedQuote?.opportunityUuid || '',
      ).trim();
      const payloadCustomerUuid = String(values.customerUuid || '').trim();
      const payloadOpportunityUuid = String(
        values.opportunityUuid || '',
      ).trim();

      if (
        quoteCustomerUuid &&
        payloadCustomerUuid &&
        quoteCustomerUuid !== payloadCustomerUuid
      ) {
        message.error(
          '当前合同客户与来源报价单客户不一致，请重新选择来源报价单或修正客户',
        );
        return;
      }
      if (
        quoteOpportunityUuid &&
        payloadOpportunityUuid &&
        quoteOpportunityUuid !== payloadOpportunityUuid
      ) {
        message.error(
          '当前合同商机与来源报价单商机不一致，请重新选择来源报价单或修正商机',
        );
        return;
      }
    }

    setSubmitting(true);
    try {
      if (mode === 'edit' && recordUuid) {
        await request(`/bff/zy-crm/contracts/${recordUuid}`, {
          method: 'PATCH',
          data: payload,
        });
        message.success(t(i18nKeys.messageUpdateSuccess, '更新成功'));
      } else {
        await request('/bff/zy-crm/contracts', {
          method: 'POST',
          data: payload,
        });
        message.success(t(i18nKeys.messageCreateSuccess, '创建成功'));
      }
      resetModalState();
      onCancel();
      await onSuccess?.();
    } finally {
      setSubmitting(false);
    }
  };

  const sharedProductRows = useMemo(
    () => editableProductRows,
    [editableProductRows],
  );

  return (
    <Modal
      closable={{ 'aria-label': '关闭' }}
      keyboard
      className={`${modalStyles.modal} ${modalStyles.form}`}
      centered
      title={
        mode === 'edit'
          ? t(i18nKeys.modalEditTitle, '编辑')
          : t(i18nKeys.modalCreateTitle, '新建')
      }
      open={open}
      width={modalWidth}
      zIndex={zIndex}
      onCancel={onCancel}
      onOk={() => void submitModal()}
      confirmLoading={submitting}
      destroyOnHidden
      forceRender
    >
      <Form
        className={modalStyles.formGrid}
        form={form}
        layout="vertical"
        colon={false}
        onValuesChange={(changedValues, allValues) => {
          if (allValues.discountPercent !== undefined) {
            form.setFieldValue('discountPercent', allValues.discountPercent);
          }
          if (sourceQuoteSyncRef.current) {
            return;
          }
          if (Object.hasOwn(changedValues, 'customerUuid')) {
            const customerUuid = String(allValues?.customerUuid || '');
            const currentSignContactUuid = String(
              allValues?.signContactUuid || '',
            ).trim();
            form.setFieldsValue({
              opportunityUuid: undefined,
              sourceQuoteUuid: undefined,
            });
            setOpportunityOptions([]);
            setQuoteOptions([]);
            setContactOptions([]);
            if (customerUuid) {
              void Promise.all([
                loadContacts('', customerUuid).then((options) => {
                  if (
                    currentSignContactUuid &&
                    !options.some(
                      (item) => item.value === currentSignContactUuid,
                    )
                  ) {
                    form.setFieldValue('signContactUuid', undefined);
                  }
                }),
                loadOpportunities('', customerUuid),
                loadQuotes('', customerUuid, undefined),
              ]);
            } else if (currentSignContactUuid) {
              form.setFieldValue('signContactUuid', undefined);
            }
          }
          if (Object.hasOwn(changedValues, 'opportunityUuid')) {
            form.setFieldValue('sourceQuoteUuid', undefined);
            setQuoteOptions([]);
            void loadQuotes(
              '',
              String(allValues?.customerUuid || ''),
              String(allValues?.opportunityUuid || ''),
            );
          }
        }}
      >
        <FormSectionDivider
          title={t(i18nKeys.formGroupBasic, '关联与基本信息')}
        />
        <Row gutter={12}>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.code}>
              <Input
                value={editingRecord?.code || ''}
                placeholder={t(
                  i18nKeys.formAutoCodeHint,
                  '根据编号规则自动生成',
                )}
                disabled
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.name}
              name="name"
              rules={[
                {
                  required: true,
                  message: t(i18nKeys.formRequired, '必填项'),
                },
              ]}
            >
              <Input
                placeholder={t(i18nKeys.formPlaceholderInput, '请输入{label}', {
                  label: fieldText.name,
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.customerUuid}
              name="customerUuid"
              rules={[
                {
                  required: true,
                  message: t(i18nKeys.formRequired, '必填项'),
                },
              ]}
            >
              <Select
                showSearch
                filterOption={false}
                options={customerOptions}
                onSearch={(value) => {
                  void loadCustomers(value);
                }}
                placeholder={t(
                  i18nKeys.formPlaceholderSelect,
                  '请选择{label}',
                  { label: fieldText.customerUuid },
                )}
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.opportunityUuid} name="opportunityUuid">
              <Select
                showSearch
                allowClear
                filterOption={false}
                options={opportunityOptions}
                disabled={initialOpportunityLocked}
                onChange={(value) =>
                  void handleOpportunityChange(String(value || ''))
                }
                onSearch={(value) => {
                  void loadOpportunities(
                    value,
                    String(form.getFieldValue('customerUuid') || ''),
                  );
                }}
                placeholder={opportunityPlaceholder}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label="来源报价单"
              name="sourceQuoteUuid"
              extra={
                selectedSourceQuote ? (
                  <Text type="secondary">
                    来源报价绑定客户：{sourceQuoteCustomerName || '-'}
                    ，来源商机：{sourceQuoteOpportunityName || '-'}
                  </Text>
                ) : null
              }
            >
              <Select
                showSearch
                allowClear
                filterOption={false}
                options={quoteOptions}
                disabled={initialSourceQuoteLocked}
                onChange={(value) =>
                  void handleQuoteChange(String(value || ''))
                }
                onSearch={(value) =>
                  void loadQuotes(
                    value,
                    String(form.getFieldValue('customerUuid') || ''),
                    String(form.getFieldValue('opportunityUuid') || ''),
                  )
                }
                placeholder={quotePlaceholder}
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.orderDate}
              name="orderDate"
              rules={[
                {
                  required: true,
                  message: t(i18nKeys.formRequired, '必填项'),
                },
              ]}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col data-crm-field="short" xs={24} md={12}>
            <Form.Item
              label={fieldText.ownerUserUuid}
              required
              validateStatus={
                form.getFieldError('ownerUserUuid').length ? 'error' : undefined
              }
              help={form.getFieldError('ownerUserUuid')[0]}
            >
              <Form.Item
                name="ownerUserUuid"
                noStyle
                rules={[
                  {
                    required: true,
                    message: t(i18nKeys.formRequired, '必填项'),
                  },
                ]}
                hidden
              >
                <Input />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, nextValues) =>
                  prevValues.ownerUserUuid !== nextValues.ownerUserUuid
                }
              >
                {() => (
                  <MemberPicker
                    token={openPlatformConfig?.m2mToken || ''}
                    value={
                      (form.getFieldValue('ownerUserUuid') as
                        | string
                        | undefined) || null
                    }
                    onChange={(next) => {
                      form.setFieldValue('ownerUserUuid', next || undefined);
                      void form
                        .validateFields(['ownerUserUuid'])
                        .catch(() => undefined);
                    }}
                    placeholder={t(
                      i18nKeys.formPlaceholderSelect,
                      '请选择{label}',
                      { label: fieldText.ownerUserUuid },
                    )}
                  />
                )}
              </Form.Item>
            </Form.Item>
          </Col>
        </Row>

        <FormSectionDivider title={t(i18nKeys.formGroupSign, '签约信息')} />
        <Row gutter={12}>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.startTime} name="startTime">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.endTime} name="endTime">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col data-crm-field="medium" xs={24} md={12}>
            {mode === 'create' || editingRecord?.contractTypeUuid ? (
              <Form.Item
                label={fieldText.contractType}
                name="contractTypeUuid"
                rules={
                  mode === 'create'
                    ? [
                        {
                          required: true,
                          message: t(i18nKeys.formRequired, '请选择{label}', {
                            label: fieldText.contractType,
                          }),
                        },
                      ]
                    : undefined
                }
              >
                <Select
                  allowClear={mode !== 'create'}
                  options={contractTypeOptions}
                  placeholder={t(
                    i18nKeys.formPlaceholderSelect,
                    '请选择{label}',
                    { label: fieldText.contractType },
                  )}
                />
              </Form.Item>
            ) : (
              <Form.Item label={fieldText.contractType} name="contractType">
                <Select
                  allowClear
                  options={CONTRACT_TYPE_OPTIONS.map((item) => ({
                    value: item.value,
                    label: t(item.key, item.fallback),
                  }))}
                />
              </Form.Item>
            )}
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.companySignerUserUuid}
              name="companySignerUserUuid"
            >
              <MemberPickerField
                adapter={orgDirectoryAdapter}
                placeholder={t(
                  i18nKeys.formPlaceholderSelect,
                  '请选择{label}',
                  { label: fieldText.companySignerUserUuid },
                )}
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.signContactUuid} name="signContactUuid">
              <Select
                showSearch
                allowClear
                filterOption={false}
                options={contactOptions}
                onSearch={(value) => {
                  void loadContacts(
                    value,
                    String(form.getFieldValue('customerUuid') || ''),
                  );
                }}
                placeholder={t(
                  i18nKeys.formPlaceholderSelect,
                  '请选择{label}',
                  { label: fieldText.signContactUuid },
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <SharedProductFormSection
          title="产品明细"
          productNameTitle={fieldText.productName}
          productTypeTitle={fieldText.productType}
          productUnitTitle={fieldText.productUnit}
          listPriceTitle={fieldText.listPrice}
          costPriceTitle={fieldText.costPrice}
          salePriceTitle={fieldText.salePrice}
          quantityTitle={fieldText.quantity}
          lineTotalTitle={fieldText.lineTotal}
          operationTitle={t(i18nKeys.columnOperation, '操作')}
          rows={sharedProductRows}
          editingRowKey={editingProductKey}
          onBeginEdit={beginEditProductRow}
          onSaveEdit={saveProductRow}
          onCancelEdit={stopProductEditing}
          onDraftChange={(patch) =>
            handleProductDraftChange((current) => ({
              ...toEditableProductRow(current),
              ...(typeof patch === 'function' ? patch(current) : patch),
            }))
          }
          onDeleteRow={deleteProductRow}
          productOptions={[]}
          productCenter={openPlatformConfig}
          onSearchProducts={() => undefined}
          loading={loading}
          scroll={{ x: 1100 }}
          emptyText={t(
            'pages.zy-crm.contract.table.emptyProducts',
            '暂无产品明细',
          )}
          addButtonText="新增一条产品"
          onAdd={addProductRow}
          showCostPrice
        />

        <FormSectionDivider title="金额汇总" />
        <Row gutter={12} style={{ marginTop: 16 }}>
          <Col data-crm-field="short" xs={24} md={12}>
            <Form.Item
              label={fieldText.discountPercent}
              name="discountPercent"
              initialValue={0}
            >
              <InputNumber
                min={0}
                max={100}
                precision={2}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.totalProductPrice}>
              <Input value={toAmount(totalProductPrice)} disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.totalPrice}>
              <Input value={toAmount(totalPrice)} disabled />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.profitAmount}>
              <Input value={toAmount(profitAmount)} disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col data-crm-field="full" xs={24}>
            <Form.Item label={fieldText.remark} name="remark">
              <Input.TextArea rows={3} maxLength={800} showCount />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
